---
title: "Trading Stock CFDs: Earnings, Gaps and Sessions"
slug: trading-stock-cfds
shortTitle: "Trading Stock CFDs"
description: "How stock CFDs work on a USDT account: exchange sessions, earnings season, overnight gaps and dividends, with worked examples of gap risk and sizing."
level: intermediate
category: markets
course: intermediate
order: 13
topics: [stocks, cfds]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/trading-stock-cfds.svg
faqs:
  - q: "Do I get shareholder rights with a stock CFD?"
    a: "No. A stock CFD pays out the change in the share price, settled in USDT. You do not own the shares, so you have no voting rights and receive no shareholder documents."
  - q: "Can I trade stock CFDs outside US market hours?"
    a: "Only during the trading hours listed for that market, which you can check on its page under Markets. Outside those hours you cannot open or close a position, and the price can move a long way before the next session starts."
  - q: "What happens to my stop loss if a stock gaps?"
    a: "A stop loss becomes a market order once the price reaches it. If the market opens beyond your stop, the stop is filled at the first available price, which can be much worse than the level you set."
  - q: "Do stock CFDs pay dividends?"
    a: "You do not receive the dividend itself, but CFD positions are commonly adjusted when a share goes ex-dividend: buy positions are usually credited and sell positions debited. Any adjustment would show in your account history."
  - q: "Is trading an index less risky than trading a single stock?"
    a: "An index spreads company-specific risk across many companies, so one earnings report moves it far less than it moves a single share. Indices still fall with the broad market, can gap and are leveraged, so the same risk rules apply."
---

A stock CFD follows the share price of a single company, such as AAPL, TSLA or NVDA, and settles the difference in USDT. You can buy if you expect the price to rise or sell if you expect it to fall, from the same account you use for crypto and gold, without owning a single share.

Shares behave differently from crypto in one important way: they only trade when their exchange is open. Much of the risk in stock CFDs comes from what happens while the market is shut, when company results and news arrive and the price can open far from where it closed. This lesson covers sessions, earnings, gaps and dividends, with the numbers worked through in USDT.

<Warning title="Stock CFDs are leveraged products">

Stock CFDs are traded on margin, so losses are magnified as much as gains and are calculated on the full position value. Individual shares can move 10% or more on a single earnings report, often as a gap your stop loss cannot fill at its level. Many traders lose money trading CFDs; only risk money you can afford to lose.

</Warning>

<CTA />

<KeyTakeaways>

- **You trade the share price, not the share.** There is no ownership or voting; profit and loss settle in USDT, and dividends are usually handled as adjustments.
- **Stocks trade in sessions.** Outside the market's trading hours you cannot open or close a position, and nights and weekends stack up news.
- **Gaps are the main hidden risk.** Earnings and overnight news can open the price beyond your stop, and a stop loss fills at the next available price.
- **Size for the gap, not only the stop.** Before holding through earnings, work out what a large gap would cost and whether you could accept it.

</KeyTakeaways>

## How a stock CFD works

You size a stock CFD like any other market. In the order panel, enter the position value in the **Amount** field in USDT, or switch to **Lots** to see how many shares one lot represents, along with the minimum, step and notional. At 180 a share, an Amount of 3,600 USDT is 3,600 ÷ 180 = 20 shares, and the **Value of $1 move** line shows 20 USDT.

Stock indices such as SPX500, NAS100 and US30 work the same way, but each one tracks a basket of companies. That changes the character of the risk. A single company can jump or slump on its own news; an index spreads that company-specific risk across many names, so it usually moves less on any one report while still following the broad market.

## Sessions: when stocks trade

Each stock exchange has set hours. The main US session runs from 9:30 to 16:00 New York time, Monday to Friday, and is closed on US market holidays. Many brokers and exchanges also offer pre-market and after-hours trading, with thinner volume and wider spreads. Whether a CFD can be traded in those extra hours depends on the market's own hours.

The open is usually the busiest and most volatile part of the day, as overnight news is priced in. Spreads tend to be wider then, and fast moves are common. The final hour before the close can also be active.

On {{BRAND}}, each market's page under Markets lists its **Trading hours** in the exchange's local time, and the trade screen shows when a market is closed. Check this before you place an order, particularly a pending order you expect to fill near the open.

{/* TODO: verify with platform — trading hours for each stock and index CFD (regular session only or extended hours), holiday closures, and how pending orders behave when the market reopens. */}

<CompareTable caption="Crypto CFDs compared with stock CFDs">

| | Crypto CFDs | Stock CFDs |
| --- | --- | --- |
| Hours | Around the clock, every day | Exchange sessions on weekdays |
| Gaps | Rare, because trading is continuous | Common at each session open |
| Scheduled shocks | Macro data, network and regulatory news | Earnings, macro data, company news |
| Swap | Charged every day | Weekdays, three times on the triple-swap day |
| Single-name risk | High for small coins | High for single shares, lower for indices |

</CompareTable>

## Earnings: the scheduled shock

Listed companies report results every quarter: revenue, **earnings per share (EPS)** and usually **guidance**, their outlook for the months ahead. Most US companies report either before the open or after the close, so the reaction often arrives as a gap at the next session.

The price reacts to results compared with expectations, not to whether the results are "good". A company can report record profits and still fall if analysts expected more or if guidance disappoints. That makes the direction of the move hard to call, even for professionals.

On {{BRAND}}, a stock's page lists the next report under **Upcoming events**, with whether it falls before the US open or after the US close, the EPS estimate where available, and a **Remind me** button that sets an alert.

<Screenshot name="stock-upcoming-earnings" alt="A stock's Upcoming events card showing the next earnings date, whether it falls after the US close, and Remind me buttons" caption="Upcoming events shows when the next earnings report is due and lets you set a reminder." />

{/* TODO: verify with platform — earnings data source and coverage for every listed stock. */}

## Gaps and your stop loss

A **gap** is a jump between one session's last price and the next session's first price, with no trading in between. Your stop loss is a market order once triggered, so if the price opens beyond it, it fills at the first available price.

<Example title="Holding through earnings (illustrative prices)">

You buy 20 shares' worth of a stock CFD at 180, a position of 20 × 180 = **3,600 USDT**, with a stop loss at 174.

- Planned loss: (180 − 174) × 20 = **120 USDT**.
- The company reports after the close and disappoints. The next session opens at 165.
- Your stop triggers at the open and fills near 165: (180 − 165) × 20 = **300 USDT** lost, 2.5 times the plan.
- At 5x leverage the trade's margin was 3,600 ÷ 5 = 720 USDT, so that one gap took about 42% of it.

</Example>

There is no way to remove this risk, only ways to manage it. You can close before the report, trade after the report once the gap has happened, or hold with a size small enough that a large gap is acceptable. If 120 USDT is the most you will lose and you think a 15-point gap is plausible, the size is 120 ÷ 15 = 8 shares, not 20.

<ProTip title="Run a gap test">

Before holding any stock CFD overnight, multiply the position value by 10%. For a 3,600 USDT position that is 360 USDT. If a loss that size would break your plan, reduce the size or close before the event.

</ProTip>

<Warning>

Margin on {{BRAND}} is shared across your account, so a gap on one stock reduces the margin backing every other position. A large enough gap can push your margin level toward {{STOP_OUT_LEVEL}}, when positions start to be closed automatically. Keep free margin in reserve when you hold shares through the night or the weekend.

</Warning>

## Dividends and costs

When a company pays a **dividend**, its share price usually drops by about the dividend amount on the **ex-dividend date**, the first day new buyers no longer qualify. CFD holders do not receive the dividend itself, but positions are commonly adjusted so the drop does not unfairly hit one side: buy positions are typically credited and sell positions debited. Corporate actions such as stock splits can also lead to adjustments.

{/* TODO: verify with platform — how dividend and corporate-action adjustments are applied to stock and index CFDs and where they appear (Portfolio → Money movements?). */}

Costs follow the usual pattern. You pay the spread, the trade fee ({{TRADE_FEE}}), and a swap on positions held past {{SWAP_HOUR}}, charged on weekdays with three days' worth on {{SWAP_TRIPLE_DAY}}. Each stock's page shows its **Swap long / short** rates. See [Trading Costs](/academy/article/trading-costs-swaps-fees) for how these add up, and [Fundamental Drivers](/academy/article/fundamental-drivers) for the economic data that moves whole markets.

## Before you trade a stock CFD

<Steps>
<Step title="Check the session">

Confirm the market is open and how long remains until the close.

</Step>
<Step title="Check upcoming events">

Look for an earnings date or major economic release before you plan to close.

</Step>
<Step title="Size for the stop and the gap">

Work out the loss at your stop, then run the gap test. Use the smaller size.

</Step>
<Step title="Set TP / SL">

Place a stop loss on every trade, as covered in [Stop Loss and Take Profit](/academy/article/stop-loss-take-profit), knowing it cannot protect you fully from a gap.

</Step>
</Steps>

## Conclusion

A stock CFD lets you trade a company's share price in either direction from your USDT account, without owning the shares. The key difference from crypto is time: stocks trade in sessions, and the most dangerous moves often happen while the market is closed. Earnings reports, overnight news and weekends can all open the price beyond your stop. Check the trading hours and upcoming events, size for the gap as well as the stop, and remember that leverage magnifies losses as much as gains.
